<template>
  <div
    class="rating-radio"
    :class="{ 'rating-radio--rated': isRated }"
    role="radiogroup"
    :aria-labelledby="labelledBy"
    :data-rating-value="modelValue"
    :tabindex="parentTabIndex"
  >
    <svg
      role="none"
      xmlns="http://www.w3.org/2000/svg"
      width="214"
      height="35"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient
          id="gradientPinkYellow"
          x1="0%"
          y1="65%"
          x2="65%"
          y2="100%"
          gradientTransform="rotate(0)"
        >
          <stop offset="0%" stop-color="var(--color-cute)" />
          <stop offset="100%" stop-color="var(--color-hachi)" />
        </linearGradient>
        <g id="star">
          <path
            d="M16 0L19.5922 11.0557H31.2169L21.8123 17.8885L25.4046 28.9443L16 22.1115L6.59544 28.9443L10.1877 17.8885L0.783095 11.0557H12.4078L16 0Z"
          />
        </g>
        <g id="focusRing">
          <rect width="34" height="32" rx="4" />
        </g>
      </defs>
      <g
        v-for="group in svgGroups"
        :ref="group.id"
        :key="group.id"
        :aria-checked="group.checked"
        :aria-label="group.label"
        :class="group.classes"
        :data-rating="group.rating"
        :role="group.role"
        :tabindex="group.tabIndex"
        @click="handleClick(group)"
        @keydown="handleKeydown"
      >
        <use
          class="star"
          xlink:href="#star"
          :x="group.starPosition.x"
          :y="group.starPosition.y"
        />
        <use
          class="focus-ring"
          xlink:href="#focusRing"
          :x="group.ringPosition.x"
          :y="group.ringPosition.y"
        />
      </g>
    </svg>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { KeyboardKey } from '~/enums/keyboard'

import type { Rating } from '~/types/RatingContextBehaviors'

interface SVGGroupData {
  id: string
  role: string
  checked: boolean
  rating: number
  label: string
  classes: string
  tabIndex: number
  starPosition: Position
  ringPosition: Position
}

interface Position {
  x: number
  y: number
}

const DEFAULT_INITIAL_STAR_POSITION_X = 2
const DEFAULT_GROUP_CLASSES = ['star-1', 'star-2', 'star-3', 'star-4', 'star-5']
const DEFAULT_STAR_WIDTH = 44

/**
 * Star slider component.
 *
 * @displayName StarSlider
 * @example
 * <StarSlider :model-value="number" />
 */
export default defineComponent({
  name: 'StarSlider',
  props: {
    /**
     * The `aria-labelledby` for the slider.
     *
     * @value should be camelCase such as `idStarSliderLabel`
     */
    labelledBy: {
      type: String,
      default: '',
    },
    /**
     * The current value of the slider.
     *
     * @value n where n is a number between 1 and total
     */
    modelValue: {
      type: Number,
      required: true,
    },
    /**
     * Whether the form that the stars belong to has been sent or not.
     */
    isRated: {
      type: Boolean,
      required: true,
    },
  },
  emits: {
    'update:modelValue': (rating: Rating) => true,
  },
  data() {
    return {
      firstRadioButton: {} as SVGGroupData,
      lastRadioButton: {} as SVGGroupData,
      parentTabIndex: -1,
      svgGroups: [] as SVGGroupData[],
      total: 5,
    }
  },
  watch: {
    modelValue(newVal: number) {
      const star = this.svgGroups.find((el) => el.rating === newVal)
      if (star) star.checked = true
    },
  },
  mounted() {
    this.initStarSlider()
  },
  methods: {
    handleClick(current: SVGGroupData) {
      this.setRating(current)
    },
    handleKeydown(event: KeyboardEvent) {
      this.keyboardNavigation(event)
    },
    initStarSlider() {
      this.createStarGroups()
      this.setCurrentTabIndex()
    },
    /**
     * Populate the `svgGroups` array with the data needed to render the svg.
     * The structure of a group is: `SVGGroupData`.
     */
    createStarGroups() {
      const length = this.total

      let positionX = 0
      let deleteCount = length

      for (let i = 1; i <= length; i++) {
        // the first x-position of the star must be defined separately,
        // then this value is incremented by the width of a star at each iteration
        positionX +=
          i === 1 ? DEFAULT_INITIAL_STAR_POSITION_X : DEFAULT_STAR_WIDTH

        const starPosition = { x: positionX, y: 2 }
        const ringPosition = { x: positionX - 1, y: 1 }

        // each group must have a specific label
        const label = this.$t('components.ui.starSlider.group.label', {
          value: i,
          total: this.total,
        })

        // each group must have a specific classes
        const classes = [...DEFAULT_GROUP_CLASSES]
          .splice(i - 1, deleteCount--)
          .join(' ')

        this.svgGroups.push({
          id: `star-${i}`,
          role: 'radio',
          checked: false,
          tabIndex: -1,
          rating: i,
          label,
          classes,
          starPosition,
          ringPosition,
        })
      }

      // save the first and last radio button
      this.svgGroups.forEach(this.setFirstAndLastRadioButton)
    },
    /**
     * Manage keyboard navigation for accessibility,
     * the supported keys are defined by `KeyboardKey`.
     *
     * @param event - {KeyboardEvent} - the event that triggered the method.
     */
    keyboardNavigation(event: KeyboardEvent) {
      const index = this.modelValue - 1 === -1 ? 0 : this.modelValue - 1
      const currentElement = this.svgGroups[index]

      let flag = false

      switch (event.key) {
        case KeyboardKey.Space:
        case KeyboardKey.Enter:
          this.setRating(currentElement)
          flag = true
          break

        case KeyboardKey.Up:
        case KeyboardKey.Left:
        case KeyboardKey.ArrowUp:
        case KeyboardKey.ArrowLeft:
          this.setRatingToPreviousItem(currentElement)
          flag = true
          break

        case KeyboardKey.Down:
        case KeyboardKey.Right:
        case KeyboardKey.ArrowDown:
        case KeyboardKey.ArrowRight:
          this.setRatingToNextItem(currentElement)
          flag = true
          break

        default:
          return
      }

      if (flag) {
        event.stopPropagation()
        event.preventDefault()
      }
    },
    /**
     * Set the current rating from a `value`.
     *
     * @param {Rating} newValue - A rating value.
     */
    setCurrentRating(newValue: Rating) {
      this.$emit('update:modelValue', newValue)
    },
    setCurrentTabIndex() {
      // Careful ! sometimes modelValue can be floating point !
      // Consider using Math.ciel/floor
      if (
        this.modelValue >= 0 &&
        this.modelValue < this.total &&
        !!this.svgGroups[this.modelValue]
      )
        this.svgGroups[this.modelValue].tabIndex = 0
      else {
        const firstIndex = this.svgGroups.indexOf(this.firstRadioButton)
        const first = this.svgGroups[firstIndex]

        this.setCurrentRating(first.rating)
        this.firstRadioButton.tabIndex = 0
      }
    },
    setFirstAndLastRadioButton(group: SVGGroupData) {
      if (Object.keys(this.firstRadioButton).length === 0)
        this.firstRadioButton = group

      this.lastRadioButton = group
    },
    /**
     * Set the current rating.
     *
     * @param current - {SVGGroupData} - the current item.
     */
    setRating(current: SVGGroupData) {
      this.parentTabIndex = -1

      // before doing something, reset all the groups
      this.svgGroups.forEach((group) => {
        group.checked = false
        group.tabIndex = -1
      })

      this.setCurrentRating(current.rating)

      // set the data for the current item
      current.checked = true
      current.tabIndex = 0

      // then we need to focus the current html element
      const currentElement = (this.$refs[current.id] as HTMLElement[])[0]
      currentElement.focus()
    },
    /**
     * Set the rating to the previous item
     * in case the first radio button is reached,
     * we will return to the last radio button.
     * This fn is used for keyboard navigation.
     *
     * @param current - {SVGGroupData} - the current group.
     */
    setRatingToPreviousItem(current: SVGGroupData) {
      if (current === this.firstRadioButton)
        this.setRating(this.lastRadioButton)
      else {
        const currentIndex = this.svgGroups.indexOf(current)
        const previousIndex = currentIndex - 1
        const previousItem = this.svgGroups[previousIndex]

        this.setRating(previousItem)
      }
    },
    /**
     * Set the rating to the next item.
     * In case the last radio button is reached,
     * we will return to the first radio button.
     * This fn is used for keyboard navigation.
     *
     * @param current - {SVGGroupData} - the current group.
     */
    setRatingToNextItem(current: SVGGroupData) {
      if (current === this.lastRadioButton)
        this.setRating(this.firstRadioButton)
      else {
        const currentIndex = this.svgGroups.indexOf(current)
        const nextIndex = currentIndex + 1
        const nextItem = this.svgGroups[nextIndex]

        this.setRating(nextItem)
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.rating-radio {
  --gradient-url: url(#gradientPinkYellow);

  @mixin fill-svg {
    @apply tw-stroke-transparent;

    fill: var(--gradient-url);
    fill-opacity: 1;
  }

  &.rating-radio--rated {
    svg .star {
      @apply tw-fill-gray-300 tw-stroke-transparent;
    }
  }

  &:focus-visible {
    @apply tw-outline-none;
  }

  &:hover {
    @apply tw-cursor-pointer;
  }

  &[data-rating-value='5'] svg .star {
    @include fill-svg;
  }

  @for $i from 1 through 4 {
    &[data-rating-value='#{$i}'] svg .star-#{$i} .star {
      @include fill-svg;
    }
  }

  label {
    @apply tw-block;
  }

  svg {
    forced-color-adjust: auto;
    touch-action: pan-y;
  }

  svg g:focus {
    @apply tw-outline-none;
  }

  svg g:focus-visible {
    @apply tw-outline-none;
  }

  svg g:focus-visible .focus-ring {
    @apply tw-stroke-black tw-stroke-2;
  }

  svg .focus-ring,
  svg .focus-ring-none {
    fill-opacity: 0;
  }

  svg .star {
    @apply tw-fill-transparent tw-stroke-gray-500;
  }
}
</style>
